import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Smile, ShieldCheck, Heart, Calendar, Check } from 'lucide-react';
const ACCENT = '#3B4A4F'; // tealslate — matches the header/footer
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function About() {
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-1514">
      {/* HERO */}
      <section className="relative pt-32 md:pt-36 pb-16 md:pb-24 px-6 md:px-10 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/wYTUiK2SU4DWFQWfeVY3zi/file_00000000c0ac71f69ffd0e1784397927.png')"
    }} data-id="element-1515">
        {/* Soft overlay to keep text legible */}
        <div className="absolute inset-0 bg-[#F9F9F9]/55 pointer-events-none" data-id="element-1516" />
        <div className="relative max-w-7xl mx-auto" data-id="element-1517">
          <div className="grid grid-cols-1 items-center" data-id="element-1518">
            {/* Left: Copy */}
            <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-xl" data-id="element-1519">
              <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
              color: ACCENT
            }} data-id="element-1520">
                About Us
              </motion.p>

              <motion.h1 variants={fadeUp} className="font-serif text-[#1F1F1F] leading-[1.05] mb-2 text-5xl md:text-6xl" data-id="element-1521">
                Rooted in Science.
              </motion.h1>
              <motion.h1 variants={fadeUp} className="font-serif italic leading-[1.05] mb-7 text-5xl md:text-6xl" style={{
              color: '#3B4A4F'
            }} data-id="element-1522">
                Focused on You.
              </motion.h1>

              <motion.div variants={fadeUp} className="w-14 h-px bg-[#1F1F1F]/30 mb-8" data-id="element-1523" />

              <motion.p variants={fadeUp} className="text-base text-[#3A3A33] leading-relaxed mb-5" data-id="element-1524">
                At Structured Nutrition, we believe real results come from
                personalized care, evidence-based strategies, and a coach who
                actually listens. Every plan starts with you, your goals, your
                routine, your medication, your life.
              </motion.p>
              <motion.p variants={fadeUp} className="text-base text-[#3A3A33] leading-relaxed" data-id="element-1525">
                Our mission is simple: help you build healthier habits, improve
                your well-being, and feel your best on GLP-1s, and long after
                you taper off.
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-4" data-id="element-1526">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-tealslate text-white text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#2A3A3F] transition-colors" data-id="element-1527">
                  Book a Consultation <ArrowRight className="w-4 h-4" data-id="element-1528" />
                </Link>
                <Link to="/services" className="inline-flex items-center justify-center px-7 py-4 border border-[#1F1F1F] text-[#1F1F1F] text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#1F1F1F] hover:text-white transition-colors" data-id="element-1529">
                  Explore Services
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-white border-y border-[#E5E5E5] py-8 md:py-10 px-6" data-id="element-1530">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-y-8 md:gap-y-0" data-id="element-1531">
          {[{
          icon: Smile,
          stat: '500+',
          label: 'Clients Helped'
        }, {
          icon: ShieldCheck,
          stat: 'Evidence-Based',
          label: 'Nutrition Guidance'
        }, {
          icon: Heart,
          stat: '100%',
          label: 'Personalized Care'
        }, {
          icon: Calendar,
          stat: 'Years of Experience',
          label: 'You Can Trust'
        }].map((item, i) => <div key={i} className={`flex items-center justify-center gap-4 px-4 ${i > 0 ? 'md:border-l md:border-[#E5E5E5]' : ''}`} data-id="element-1532">
              <item.icon className="w-9 h-9 text-[#3B4A4F] shrink-0" strokeWidth={1.3} data-id="element-1533" />
              <div className="text-left" data-id="element-1534">
                <p className="text-base md:text-lg font-serif text-[#1F1F1F] leading-tight" data-id="element-1535">
                  {item.stat}
                </p>
                <p className="text-xs md:text-sm text-[#5A5A50] leading-tight mt-0.5" data-id="element-1536">
                  {item.label}
                </p>
              </div>
            </div>)}
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-[#F9F9F9]" data-id="element-1537">
        <div className="grid grid-cols-1 lg:grid-cols-[43%_57%] items-stretch lg:h-[375px]" data-id="element-1538">
          {/* Left: Client quote (gray box) */}
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.8,
          ease: [0.2, 0.65, 0.3, 0.9]
        }} className="w-full h-full min-h-[320px] bg-[#E8E8E5] flex items-center justify-center px-8 md:px-12" data-id="element-1539">
            <figure className="max-w-md text-center" data-id="element-1540">
              <span className="block font-serif text-5xl leading-none text-[#9A9A8E] mb-3" aria-hidden="true" data-id="element-1541">
                &ldquo;
              </span>
              <blockquote className="font-serif italic text-lg md:text-xl text-[#1F1F1F] leading-snug mb-5" data-id="element-1542">
                Working with Structured Nutrition changed everything. I finally
                understood how to eat with my medication instead of fighting it,
                and the results have lasted long after I tapered off.
              </blockquote>
              <figcaption className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5A5A50]" data-id="element-1543">
                Client
              </figcaption>
            </figure>
          </motion.div>

          {/* Right: Copy */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.3
        }} variants={stagger} className="max-w-3xl px-6 md:px-12 mx-auto lg:mx-0 py-12 lg:py-0 self-center" data-id="element-1544">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-1545">
              Our Story
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-[#1F1F1F] leading-[1.15] text-2xl md:text-3xl mb-5" data-id="element-1546">
              A passion for people. A commitment to results.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed mb-4" data-id="element-1547">
              Structured Nutrition was founded with one goal in mind: to make
              high-quality GLP-1 nutrition care accessible, personalized, and
              effective. We've helped hundreds of clients overcome challenges,
              build confidence, and create sustainable habits that last well
              beyond the medication.
            </motion.p>
            <motion.p variants={fadeUp} className="text-sm text-[#1F1F1F] font-bold leading-relaxed mb-7" data-id="element-1548">
              Your health journey is unique — your plan should be too.
            </motion.p>
            <motion.div variants={fadeUp} data-id="element-1549">
              <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors" style={{
              backgroundColor: '#3B4A4F'
            }} data-id="element-1550">
                Ready To Learn More? <ArrowRight className="w-4 h-4" data-id="element-1551" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TEAM / APPROACH */}
      <section className="bg-white py-16 md:py-20 px-6 md:px-10" data-id="element-1552">
        <div className="max-w-7xl mx-auto" data-id="element-1553">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center" data-id="element-1554">
            {/* Left: Headshot + signature */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.7,
            ease: [0.2, 0.65, 0.3, 0.9]
          }} className="md:col-span-3 flex flex-col items-center md:items-start" data-id="element-1555">
              <div className="w-32 h-32 md:w-36 md:h-36 rounded-full overflow-hidden bg-[#E8E4D8] mb-5" data-id="element-1556">
                <img src="https://cdn.magicpatterns.com/uploads/fBWp7VcHydeQL4QeV4Xzp2/pasted-image.png" alt="Jack Achenbach — Structured Nutrition" className="w-full h-full object-cover" style={{
                objectPosition: '42% center'
              }} data-id="element-1557" />
              </div>
              <p className="text-3xl text-[#3A3A33] mb-1" style={{
              fontFamily: '"Dancing Script", "Brush Script MT", cursive'
            }} data-id="element-1558">
                With care,
              </p>
              <p className="text-sm font-bold text-[#1F1F1F] uppercase tracking-wide" data-id="element-1559">
                The Structured Nutrition Team
              </p>
            </motion.div>

            {/* Middle: Copy */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.3
          }} variants={stagger} className="md:col-span-5" data-id="element-1560">
              <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-4 text-[#1F1F1F]" data-id="element-1561">
                Our Team
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-serif text-[#1F1F1F] leading-[1.15] text-2xl md:text-3xl mb-5" data-id="element-1562">
                Built on experience. Driven by purpose.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed mb-4" data-id="element-1563">
                Structured Nutrition was founded by a team of nutrition
                professionals who saw a need for a more sustainable,
                personalized, and realistic approach to healthy living.
              </motion.p>
              <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed" data-id="element-1564">
                We're here to cut through the noise and help you build a
                healthier relationship with food, one step at a time.
              </motion.p>
            </motion.div>

            {/* Right: Check list */}
            <motion.ul initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.3
          }} variants={stagger} className="md:col-span-4 space-y-6" data-id="element-1565">
              {['Registered & Qualified Nutrition Professionals', 'Ongoing Education & Training', 'Focused on Real Results'].map((item, i) => <motion.li key={i} variants={fadeUp} className="flex items-center gap-4" data-id="element-1566">
                  <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{
                backgroundColor: '#3B4A4F'
              }} data-id="element-1567">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1568" />
                  </span>
                  <span className="text-sm md:text-base font-bold text-[#1F1F1F] leading-snug" data-id="element-1569">
                    {item}
                  </span>
                </motion.li>)}
            </motion.ul>
          </div>
        </div>
      </section>
    </div>;
}